import { Suspense } from 'react';

import { getAccountHasPendingManualRepayment } from '@helpers/Accounts/getAccountHasPendingManualRepayment';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { IconTimesDefault } from '@iwoca/icons';
import {
    fetchGetAccounts,
    fetchPostManualRepaymentIntentCancel
} from '@iwoca/lapi-client/edge';
import { AlertBox } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { CardPendingSVG } from '@svgComponents/CardPendingSVG';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';

export const PendingManualRepaymentBanner = ({
    accountId
}: {
    accountId: string;
}) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <Skeleton
                        data-testid="pending-manual-repayment-banner-skeleton"
                        className="h-[60px] w-full rounded-s"
                    />
                }
            >
                <PendingManualRepayment accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const PendingManualRepayment = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const { data: account } = useQuery({
        queryKey: ['accounts', stateKey],
        queryFn: () => fetchGetAccounts({ stateKey }),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    const cancelManualRepaymentMutation = useMutation({
        mutationFn: () =>
            fetchPostManualRepaymentIntentCancel({
                stateKey: stateKey!,
                accountId
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accounts', stateKey] });
        },
        onError: () => {
            logError(
                `Failed to cancel manual repayment intent for account_id: ${accountId}.`,
                {
                    tags: TAGS.DE_LENDING
                }
            );
        }
    });

    if (!account) {
        const errorMessage = `PendingManualRepaymentBanner - Account with account_id: ${accountId} not found`;

        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const { hasPendingManualRepayment, pendingManualRepaymentAmount } =
        getAccountHasPendingManualRepayment(account);

    if (!hasPendingManualRepayment) return null;

    return (
        <AlertBox
            variant="connect"
            className="flex flex-row items-start gap-m"
            data-testid="pending-manual-repayment-banner"
        >
            <CardPendingSVG />
            <div className="flex-1">
                <Text
                    data-testid="pending-manual-repayment-title"
                    className="text-s font-bold"
                >
                    <FormattedMessage
                        id="_.outstanding_view.pending_manual_repayment_banner_title"
                        defaultMessage="Refunds usually appear in your account within 1 working day."
                    />
                </Text>
                <Text className="text-s">
                    <FormattedMessage
                        id="_.outstanding_view.pending_manual_repayment_banner_description"
                        defaultMessage="You have indicated that you would like to transfer {repaymentAmount} to us. If you have changed your mind, you can simply close this message."
                        values={{
                            repaymentAmount: (
                                <FormattedCurrency
                                    value={pendingManualRepaymentAmount}
                                    decimalPlaces={2}
                                />
                            )
                        }}
                    />
                </Text>
            </div>
            <button
                data-testid="cancel-manual-repayment-button"
                onClick={() => cancelManualRepaymentMutation.mutate()}
                disabled={cancelManualRepaymentMutation.isLoading}
                className="rounded p-xs hover:opacity-75"
            >
                <IconTimesDefault />
            </button>
        </AlertBox>
    );
};
